import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Star, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

import { productUrl } from '../lib/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductCard = ({ product, onFavoriteToggle }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const [isFavorite, setIsFavorite] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const inStock = (product.stock || 0) > 0 || (product.seller_count || 0) > 0 || product.has_seller_stock;
  const displayPrice = product.display_price || product.best_price || (product.base_price ? product.base_price * 1.62 : 0);

  const addToGuestCart = (product) => {
    const guestCart = JSON.parse(localStorage.getItem('pazarlo_guest_cart') || '[]');
    const existing = guestCart.find(item => item.id === product.id);
    
    if (existing) {
      existing.quantity = Math.min(existing.quantity + 1, product.stock || 99);
    } else {
      guestCart.push({
        id: product.id,
        name: product.name,
        brand: product.brand,
        oem_code: product.oem_code,
        price: displayPrice,
        display_price: displayPrice,
        base_price: product.base_price,
        image_url: product.image_url || product.images?.[0],
        stock: product.stock || 99,
        quantity: 1
      });
    }
    
    localStorage.setItem('pazarlo_guest_cart', JSON.stringify(guestCart));
    return guestCart;
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!inStock) {
      toast.error('Ürün stokta yok');
      return;
    }

    if (!isAuthenticated) {
      addToGuestCart(product);
      toast.success('Ürün sepete eklendi!', {
        action: {
          label: 'Sepete Git',
          onClick: () => navigate('/guest-checkout')
        }
      });
      return;
    }

    setLoading(true);
    try {
      await addToCart(product.id, 1);
      toast.success('Ürün sepete eklendi');
    } catch (error) {
      toast.error('Ürün eklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Favorilere eklemek için giriş yapın');
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${product.id}`);
        setIsFavorite(false);
        toast.success('Favorilerden kaldırıldı');
      } else {
        await axios.post(`${API}/favorites/${product.id}`);
        setIsFavorite(true);
        toast.success('Favorilere eklendi');
      }
      onFavoriteToggle?.();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-3 w-3 ${i < Math.round(rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <Link 
      to={productUrl(product)}
      className="product-card rounded-xl border overflow-hidden block hover:shadow-lg transition-shadow"
      style={{ backgroundColor: '#FFFFFF' }}
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative aspect-square" style={{ backgroundColor: '#FFFFFF' }}>
        {(product.image_url || product.image || (product.images && product.images.length > 0)) ? (
          <img
            src={product.image_url || product.image || product.images?.[0]}
            alt={product.name}
            className="w-full h-full object-contain p-2"
            style={{ backgroundColor: '#FFFFFF', mixBlendMode: 'multiply' }}
            onError={(e) => { 
              e.target.onerror = null;
              e.target.style.display = 'none';
              if(e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="w-full h-full items-center justify-center text-slate-300" style={{display: (product.image_url || product.image || product.images?.[0]) ? 'none' : 'flex', backgroundColor: '#FFFFFF'}}>
          <ShoppingCart className="h-16 w-16" />
        </div>
        
        {/* Favorite Button */}
        <button
          onClick={handleFavorite}
          className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
            isFavorite ? 'bg-red-500 text-white' : 'bg-white/90 text-slate-600 hover:bg-white'
          }`}
          data-testid={`favorite-btn-${product.id}`}
        >
          <Heart className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
        </button>
      </div>

      {/* Content */}
      <div className="p-4" style={{ backgroundColor: '#FFFFFF' }}>
        {/* Product Name */}
        <h3 className="font-semibold text-slate-900 line-clamp-3 mb-2 text-sm min-h-[60px]">
          {product.name}
        </h3>

        {/* OEM Code - Üstte */}
        {(product.oem_code || product.oem_number || product.sku) && (
          <div className="text-xs text-slate-500 mb-1">
            OEM: {product.oem_code || product.oem_number || product.sku}
          </div>
        )}

        {/* Brand - OEM'in altında */}
        {product.brand && (
          <div className="mb-2">
            <span className="text-xs font-medium bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
              {product.brand}
            </span>
          </div>
        )}

        {/* Rating */}
        <div className="flex items-center gap-2 mb-2">
          <div className="flex">{renderStars(product.rating)}</div>
          <span className="text-xs text-slate-500">
            ({product.review_count || 0})
          </span>
        </div>

        {/* Stok Durumu */}
        <div className="mb-3">
          {inStock ? (
            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
              Stokta var
            </span>
          ) : (
            <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded">
              Stokta yok
            </span>
          )}
        </div>

        {/* Price & Cart */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {displayPrice > 0 ? (
              <span className="text-lg font-bold text-orange-600">
                {formatPrice(displayPrice)}
              </span>
            ) : (
              <span className="text-sm text-slate-400">Fiyat Sorunuz</span>
            )}
          </div>
          
          {displayPrice > 0 && (
            <Button
              size="sm"
              onClick={handleAddToCart}
              disabled={loading || !inStock}
              className={inStock ? "bg-orange-500 hover:bg-orange-600" : "bg-gray-300 cursor-not-allowed"}
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingCart className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
